/**
 * Operations Manual — sidebar download/open link.
 *
 * Why this exists as its own component:
 *   The previous inline implementation had four cross-platform bugs
 *   that all silently produced "nothing happens" for the operator:
 *
 *     1. Wrong localStorage key (read 'token' instead of 'yulontap_token')
 *        ⇒ fetch sent `Authorization: Bearer null` ⇒ 403 silent.
 *     2. window.open(blobUrl) inside an async .then() callback
 *        ⇒ blocked by popup blockers AND completely broken on iOS Safari
 *          (which does not accept blob: URLs in window.open at all).
 *     3. No error handling ⇒ failures invisible.
 *     4. No loading state ⇒ 400 KB blob took ~1 s with no UI feedback.
 *
 *   The new approach uses the universal `<a download>` programmatic-
 *   click pattern, which works identically on:
 *     - Desktop Chrome / Edge / Firefox / Safari → file lands in Downloads.
 *     - iPad / iPhone Safari → Files app or Share Sheet picker.
 *     - Android Chrome → notification + download to /Download/.
 *
 *   A "View in browser" affordance is provided for desktop operators
 *   who'd rather read inline; it opens a blob URL in a new tab. We do
 *   NOT attempt that on touch devices, where it fails silently.
 */
import { useState } from "react";
import { BookOpen, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import { getToken } from "../../lib/session";

async function fetchManualBlob() {
    const token = getToken();
    if (!token) {
        throw new Error("not_authenticated");
    }
    // Runtime origin — same pattern as `lib/api.js`. Avoids CRA baking a
    // stale `REACT_APP_BACKEND_URL` from build time into the production
    // bundle (which caused CORS failures on portal.yulontap.com when the
    // build was run against a preview env).
    const url = `${window.location.origin}/api/docs/operational-manual/pdf`;
    const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
        // Don't send cookies — bearer-token only.
        credentials: "omit",
    });
    if (!res.ok) {
        throw new Error(`http_${res.status}`);
    }
    return res.blob();
}

function triggerDownload(blob, filename) {
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    // Some browsers require the anchor to be in the DOM before clicking.
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoke a tick later — Safari needs the URL alive long enough to
    // start the download.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

export default function OpsManualLink() {
    const { t } = useTranslation();
    const { hasPermission } = useAuth();
    const [busy, setBusy] = useState(false);

    // v4.32.0 — Phase 4 — Documentation visibility is permission-gated.
    // When `ops_manual_access` is False (the default for every non-Super
    // role), the sidebar link is fully hidden — not greyed out, not
    // disabled, simply absent. The backend mirrors this with a 403 on
    // every download endpoint and writes an audit row on each denial.
    if (!hasPermission("ops_manual_access")) {
        return null;
    }

    const handleClick = async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        toast.loading(t("ops_manual.preparing", "Preparing manual…"), {
            id: "ops-manual-loading",
        });

        try {
            const blob = await fetchManualBlob();
            toast.dismiss("ops-manual-loading");

            const filename = "YUL_OnTap_Operational_Manual.pdf";

            // Universal download path — works identically on Chrome /
            // Firefox / Safari / Edge / iOS Safari / Android. Avoids the
            // popup-blocker + iOS-Safari-blob-window-open trap.
            triggerDownload(blob, filename);
            toast.success(t("ops_manual.downloaded", "Manual downloaded."));
        } catch (err) {
            toast.dismiss("ops-manual-loading");
            const code = err?.message || "unknown";
            const msg =
                code === "not_authenticated"
                    ? t("ops_manual.err_auth", "Please sign in again to access the manual.")
                    : code === "http_403"
                    ? t("ops_manual.err_403", "You don't have permission to access the manual.")
                    : code === "http_404"
                    ? t("ops_manual.err_404", "Manual is being built. Try again in a minute.")
                    : t("ops_manual.err_generic", "Couldn't load the manual. Please try again.");
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <a
            href="#"
            role="button"
            onClick={handleClick}
            data-testid="ops-manual-link"
            aria-busy={busy ? "true" : "false"}
            className="sidebar-link w-full cursor-pointer"
            title={t("nav.ops_manual", "Operations Manual")}
        >
            {busy ? (
                <Loader2 size={20} strokeWidth={1.85} className="animate-spin" />
            ) : (
                <BookOpen size={20} strokeWidth={1.85} />
            )}
            <span className="truncate whitespace-nowrap">{t("nav.ops_manual", "Operations Manual")}</span>
        </a>
    );
}
